import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CONTEXT_NEAR_LIMIT_PERCENT, type ContextPressure, type RunawayReason, type SessionPoint, type SessionsSummary } from '@ace-tracker/shared';
import { chartAxisTickProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { SESSIONS_METRIC_INFO } from '../metricInfo';
import { TOOL_COLORS } from '../toolColors';
import { ChartCard } from './ChartCard';
import { InfoTooltip } from './InfoTooltip';
import { PricingBasisNote } from './PricingBasisNote';
import { SubagentSection } from './SubagentSection';
import { CopilotSessionsSection } from './CopilotSessionsSection';
import { ToolCallsSection } from './ToolCallsSection';
import { Modal } from './Modal';
import { DeveloperName } from '../settings';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

type Show = 'all' | 'runaway';
const DASH = '—';
const REASON_LABELS: Record<RunawayReason, string> = { cost: 'Cost', duration: 'Duration', requests: 'Requests' };

function formatDuration(minutes: number): string {
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  return `${Math.floor(minutes / 60)} h ${Math.round(minutes % 60)} min`;
}

const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function formatContext(tokens: number, percent: number | null): string {
  if (tokens <= 0) return DASH;
  return percent === null ? `${compact.format(tokens)} tokens` : `${Math.round(percent)}%`;
}

function bandLabel(from: number, to: number | null): string {
  return to === null ? `>${from}%` : `${from}–${to}%`;
}

/**
 * Sessions view: per-session indicative cost, duration and requests for
 * Claude Code and Codex, with each tool's extreme outliers flagged as
 * runaway. Each session shows the developer who ran it (display name from Settings → Developers).
 */
export function SessionsDashboardBody({
  summary,
  formatCurrency,
  SectionHeading,
}: {
  summary: SessionsSummary;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const [show, setShow] = useState<Show>('all');
  const [open, setOpen] = useState<SessionPoint | null>(null);
  const money = (value: number) => formatCurrency(value, summary.currency);
  const rows = show === 'runaway' ? summary.sessions.filter((s) => s.runawayReasons.length > 0) : summary.sessions;
  const runawayTotal = summary.stats.reduce((sum, s) => sum + s.runaway, 0);
  const copilotSection = summary.copilot && <CopilotSessionsSection copilot={summary.copilot} SectionHeading={SectionHeading} />;
  const toolCallsSection = summary.toolCalls && <ToolCallsSection toolCalls={summary.toolCalls} SectionHeading={SectionHeading} />;

  if (summary.totalSessions === 0) {
    return (
      <>
        <p className="text-sm text-text-muted">No Claude Code or Codex sessions in this range.</p>
        {copilotSection}
        {toolCallsSection}
      </>
    );
  }

  return (
    <>
      <section>
        <SectionHeading title="At a glance" hint="Sessions with a recorded session id" info={SESSIONS_METRIC_INFO.runaway} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {summary.stats.map((s) => (
            <div key={s.tool} className="relative rounded-card border border-border bg-surface p-5 shadow-sm">
              <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TOOL_COLORS[s.tool] }} />
              <h3 className="text-sm font-semibold text-text-default">{TOOL_LABELS[s.tool]}</h3>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm sm:grid-cols-4">
                <Metric label="Sessions" value={s.sessions.toLocaleString()} />
                <Metric label="Median cost" value={money(s.medianCost)} />
                <Metric label="95th percentile" value={money(s.p95Cost)} />
                <Metric label="Runaway" value={s.thresholds ? s.runaway.toLocaleString() : DASH} />
              </dl>
              <p className="mt-3 text-xs text-text-muted">
                Median {formatDuration(s.medianDurationMinutes)} and {Math.round(s.medianRequests)} requests per session.{' '}
                {s.thresholds
                  ? `Runaway above ${money(s.thresholds.cost)}, ${formatDuration(s.thresholds.durationMinutes)} or ${Math.round(s.thresholds.requests)} requests.`
                  : 'Too few sessions to flag runaways.'}
              </p>
            </div>
          ))}
        </div>
      </section>

      {summary.contextPressure.length > 0 && (
        <section>
          <SectionHeading
            title="Context pressure"
            hint={`Largest context per turn as a share of the model's window · near the limit = ${CONTEXT_NEAR_LIMIT_PERCENT}% or more`}
            info={SESSIONS_METRIC_INFO.contextPressure}
          />
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {summary.contextPressure.map((p) => (
              <ContextPressureChart key={p.tool} pressure={p} />
            ))}
          </div>
        </section>
      )}

      {summary.subagents.map((s) => (
        <SubagentSection key={s.tool} subagents={s} sessions={summary.sessions} money={money} onOpenSession={setOpen} SectionHeading={SectionHeading} />
      ))}

      {toolCallsSection}

      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <SectionHeading
            title="Sessions"
            hint={`Showing ${summary.sessions.length} of ${summary.totalSessions} — every runaway session plus the costliest others`}
          />
          <div role="group" aria-label="Show sessions" className="inline-flex rounded-pill border border-border bg-surface p-0.5">
            {(['all', 'runaway'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={show === option}
                onClick={() => setShow(option)}
                className={`min-h-[36px] rounded-pill px-4 text-xs font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue ${
                  show === option ? 'bg-primary-blue text-text-inverse' : 'text-text-muted hover:bg-surface-alt hover:text-text-default'
                }`}
              >
                {option === 'all' ? 'All' : `Runaway only (${runawayTotal})`}
              </button>
            ))}
          </div>
        </div>
        <TableCard title="Sessions by cost" info={SESSIONS_METRIC_INFO.list} empty={rows.length === 0} emptyText="No runaway sessions in this range.">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Session</th>
              <th className="pb-2.5 pr-3">Developer</th>
              <th className="pb-2.5 pr-3">Tool</th>
              <th className="pb-2.5 pr-3">Project</th>
              <th className="pb-2.5 pr-3">Started</th>
              <th className="pb-2.5 pr-3 text-right">Requests</th>
              <th className="pb-2.5 pr-3 text-right">Duration</th>
              <th className="pb-2.5 pr-3 text-right" title="Largest single-turn context as a share of the model's window">Peak context</th>
              <th className="pb-2.5 pr-3 text-right">Cost</th>
              <th className="pb-2.5">Runaway on</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={`${s.tool}:${s.sessionId}`} className="border-b border-border last:border-0">
                <td className="py-2 pr-3">
                  <button
                    type="button"
                    onClick={() => setOpen(s)}
                    aria-label={`Show details of session ${s.sessionId}`}
                    className="min-h-[36px] font-mono text-xs text-primary-blue underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
                  >
                    {s.sessionId.slice(0, 8)}
                  </button>
                </td>
                <td className="py-2 pr-3 text-text-default">{s.developerId ? <DeveloperName developerId={s.developerId} /> : DASH}</td>
                <td className="py-2 pr-3 text-text-muted">{TOOL_LABELS[s.tool]}</td>
                <td className="max-w-[180px] truncate py-2 pr-3 text-text-muted" title={s.projectId ?? undefined}>{s.projectId ?? DASH}</td>
                <td className="py-2 pr-3 tabular-nums text-text-muted">{new Date(s.firstSeen).toLocaleString()}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{s.requests.toLocaleString()}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{formatDuration(s.durationMinutes)}</td>
                <td className="py-2 pr-3 text-right tabular-nums" title={s.peakContextTokens > 0 ? `${s.peakContextTokens.toLocaleString()} tokens` : undefined}>
                  {s.peakContextPercent !== null && s.peakContextPercent >= CONTEXT_NEAR_LIMIT_PERCENT && (
                    <i aria-hidden="true" className="fa-solid fa-triangle-exclamation mr-1 text-warn-ink" />
                  )}
                  {formatContext(s.peakContextTokens, s.peakContextPercent)}
                </td>
                <td className="py-2 pr-3 text-right font-medium tabular-nums text-text-default">{money(s.cost)}</td>
                <td className="py-2">
                  {s.runawayReasons.length === 0 ? (
                    <span className="text-text-faint">{DASH}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px] text-text-default">
                      <i aria-hidden="true" className="fa-solid fa-triangle-exclamation text-warn-ink" />
                      {s.runawayReasons.map((r) => REASON_LABELS[r]).join(', ')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      </section>

      {copilotSection}

      <PricingBasisNote
        basis={summary.pricing}
        totalCost={summary.stats.reduce((sum, s) => sum + s.totalCost, 0)}
        unpricedModels={summary.unpricedModels}
      >
        Usage recorded without a session id is not included here, so totals can be below the Spend view.
      </PricingBasisNote>

      <Modal open={open !== null} onClose={() => setOpen(null)} title={open ? `Session ${open.sessionId}` : 'Session'}>
        {open && <SessionDetail session={open} money={money} />}
      </Modal>
    </>
  );
}

function ContextPressureChart({ pressure }: { pressure: ContextPressure }) {
  const known = pressure.turns - pressure.unknownWindowTurns;
  const data = pressure.bands.map((b) => ({ band: bandLabel(b.from, b.to), share: known > 0 ? (b.turns / known) * 100 : 0, turns: b.turns, near: b.from >= CONTEXT_NEAR_LIMIT_PERCENT }));
  const nearShare = known > 0 ? (pressure.nearLimitTurns / known) * 100 : null;
  return (
    <ChartCard
      title={TOOL_LABELS[pressure.tool]}
      caption={`${pressure.turns.toLocaleString()} turns · ${nearShare === null ? 'no model with a known window' : `${nearShare.toFixed(1)}% near the limit`}`}
      info={SESSIONS_METRIC_INFO.contextPressure}
      height="h-72"
    >
      <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" vertical={false} />
          <XAxis dataKey="band" {...chartXAxisTickProps} />
          <YAxis {...chartAxisTickProps} width={44} tickFormatter={(v: number) => `${Math.round(v)}%`} />
          <Tooltip {...chartTooltipProps} formatter={((value: number) => `${value.toFixed(1)}% of turns`) as never} />
          <Bar dataKey="share" name="Share of turns" isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.band} fill={d.near ? 'var(--warning)' : TOOL_COLORS[pressure.tool]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </div>
      {pressure.modelsWithoutWindow.length > 0 && (
        <p className="mt-1 text-[11px] text-text-muted">
          No context window known for {pressure.modelsWithoutWindow.join(', ')} ({pressure.unknownWindowTurns.toLocaleString()} turns) — set one in Settings → Model pricing.
        </p>
      )}
      </div>
    </ChartCard>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-text-faint">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-text-default">{value}</dd>
    </div>
  );
}

function SessionDetail({ session, money }: { session: SessionPoint; money: (value: number) => string }) {
  const n = (value: number) => value.toLocaleString();
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm sm:grid-cols-4">
        <Metric label="Tool" value={TOOL_LABELS[session.tool]} />
        <Metric label="Cost" value={money(session.cost)} />
        <Metric label="Requests" value={n(session.requests)} />
        <Metric label="Duration" value={formatDuration(session.durationMinutes)} />
        <div className="col-span-2">
          <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-text-faint">
            Subagent requests
            <InfoTooltip text={SESSIONS_METRIC_INFO.subagents} />
          </dt>
          <dd className="text-lg font-semibold tabular-nums text-text-default">
            {session.subagentRequests === null ? 'Not recorded' : n(session.subagentRequests)}
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-[11px] uppercase tracking-wide text-text-faint">Developer</dt>
          <dd className="truncate text-sm text-text-default">{session.developerId ? <DeveloperName developerId={session.developerId} showTeam /> : DASH}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-[11px] uppercase tracking-wide text-text-faint">Project</dt>
          <dd className="truncate text-sm text-text-default" title={session.projectId ?? undefined}>{session.projectId ?? DASH}</dd>
        </div>
      </dl>
      <p className="text-xs text-text-muted">
        {new Date(session.firstSeen).toLocaleString()} – {new Date(session.lastSeen).toLocaleString()}
        {session.runawayReasons.length > 0 && ` · Runaway on ${session.runawayReasons.map((r) => REASON_LABELS[r].toLowerCase()).join(', ')}`}
      </p>
      <TableCard title="Tokens by model" info={SESSIONS_METRIC_INFO.tokens} empty={session.byModel.length === 0} emptyText="No model usage.">
        <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
          <tr>
            <th className="pb-2.5 pr-3">Model</th>
            <th className="pb-2.5 pr-3 text-right">Requests</th>
            <th className="pb-2.5 pr-3 text-right">Input</th>
            <th className="pb-2.5 pr-3 text-right">Output</th>
            <th className="pb-2.5 pr-3 text-right">Reasoning</th>
            <th className="pb-2.5 pr-3 text-right">Cache read</th>
            <th className="pb-2.5 pr-3 text-right">Cache write</th>
            <th className="pb-2.5 pr-3 text-right">Peak context</th>
            <th className="pb-2.5 text-right">Cost</th>
          </tr>
        </thead>
        <tbody>
          {session.byModel.map((m) => (
            <tr key={m.model} className="border-b border-border last:border-0">
              <td className="py-2 pr-3 text-text-default">
                {m.model}
                {m.pricedAs === null && ' '}
                {m.pricedAs === null && <span className="rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px]">Fallback rate</span>}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{n(m.requests)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{n(m.tokens.input)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{n(m.tokens.output)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{n(m.tokens.reasoning)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{n(m.tokens.cacheRead)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{session.tool === 'codex' ? DASH : n(m.tokens.cacheWrite)}</td>
              <td className="py-2 pr-3 text-right tabular-nums">
                {m.maxContextTokens > 0 ? n(m.maxContextTokens) : DASH}
                {m.contextWindow && m.maxContextTokens > 0 && (
                  <span className="text-text-faint"> ({Math.round((m.maxContextTokens / m.contextWindow) * 100)}% of {compact.format(m.contextWindow)})</span>
                )}
              </td>
              <td className="py-2 text-right font-medium tabular-nums">{money(m.cost)}</td>
            </tr>
          ))}
        </tbody>
      </TableCard>
    </div>
  );
}
